'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import {
  Marquee,
  MarqueeContent,
  MarqueeFade,
  MarqueeItem,
} from '@/components/kibo-ui/marquee';

import { TESTIMONIALS_1, TESTIMONIALS_2 } from '../../data/testimonials';
import type { Testimonial } from '../../types/testimonials';
import { Panel } from '../panel';
import { TestimonialItem } from './testimonial-item';

const RESUME_AFTER_TOUCH_MS = 3000;

const byAuthorName = (a: Testimonial, b: Testimonial) =>
  a.authorName.localeCompare(b.authorName);

function subscribeToReducedMotion(callback: () => void) {
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
}

function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false
  );
}

export function TestimonialsMarquee() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // react-fast-marquee's autoFill duplicates every card to fill the track;
  // keep the clones out of the tab order and away from screen readers so
  // each testimonial is announced and focusable exactly once.
  useEffect(() => {
    if (prefersReducedMotion) return;
    const container = containerRef.current;
    if (!container) return;

    const hideClones = () => {
      for (const link of container.querySelectorAll('a')) {
        const isOriginal = link.closest('.rfm-initial-child-container');
        if (!isOriginal && link.tabIndex !== -1) {
          link.tabIndex = -1;
          link.setAttribute('aria-hidden', 'true');
        }
      }
    };

    hideClones();
    // autoFill re-clones on resize; re-apply when the track re-renders.
    const observer = new MutationObserver(hideClones);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  const permissionNote = (
    <p className="px-4 py-2 text-center font-mono text-xs text-muted-foreground">
      Testimonials reproduced with permission.
    </p>
  );

  // Static, readable list for users who prefer reduced motion.
  if (prefersReducedMotion) {
    return (
      <Panel id="testimonials">
        <h2 className="sr-only">Testimonials</h2>

        <ul className="grid grid-cols-1 sm:grid-cols-2">
          {[...TESTIMONIALS_1, ...TESTIMONIALS_2]
            .sort(byAuthorName)
            .map((item) => (
              <li
                key={item.authorName}
                className="border-b border-edge sm:odd:border-r"
              >
                <TestimonialItem {...item} />
              </li>
            ))}
        </ul>

        {permissionNote}
      </Panel>
    );
  }

  const pause = () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    setPaused(true);
  };

  const handleTouchEnd = () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(
      () => setPaused(false),
      RESUME_AFTER_TOUCH_MS
    );
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setPaused(false);
    }
  };

  return (
    <Panel
      id="testimonials"
      className="before:z-11 after:z-10 [&_.rfm-initial-child-container]:items-stretch! [&_.rfm-marquee]:items-stretch!"
    >
      <h2 className="sr-only">Testimonials</h2>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: listeners only pause the marquee animation; the wrapper itself is not interactive */}
      <div
        ref={containerRef}
        onFocus={pause}
        onBlur={handleBlur}
        onTouchStart={pause}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <Marquee>
          <MarqueeFade side="left" />
          <MarqueeFade side="right" />

          <MarqueeContent play={!paused}>
            {TESTIMONIALS_1.slice()
              .sort(byAuthorName)
              .map((item, index) => (
                <MarqueeItem
                  key={`${item.authorName}-${index}`}
                  className="mx-0 h-full w-xs border-r border-edge"
                >
                  <TestimonialItem {...item} />
                </MarqueeItem>
              ))}
          </MarqueeContent>
        </Marquee>

        <div className="screen-line-before screen-line-after relative flex h-4 w-full" />

        <Marquee>
          <MarqueeFade side="left" />
          <MarqueeFade side="right" />

          <MarqueeContent direction="right" play={!paused}>
            {TESTIMONIALS_2.slice()
              .sort(byAuthorName)
              .map((item, index) => (
                <MarqueeItem
                  key={`${item.authorName}-${index}`}
                  className="mx-0 h-full w-xs border-r border-edge"
                >
                  <TestimonialItem {...item} />
                </MarqueeItem>
              ))}
          </MarqueeContent>
        </Marquee>
      </div>

      {permissionNote}
    </Panel>
  );
}
